<template>
    <v-container fluid>
        <div class="action-bar">
            <v-row>
                <v-col>
                    <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="saveClick(null)">
                        <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
                    </v-btn>
                    <v-btn v-if="!inEditMode" class="ml-2 mr-2" color="primary" @click="editClick()">
                        <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                    </v-btn>
                    <v-btn class="mr-2" @click="backClick">
                        <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                    </v-btn>
                </v-col>
            </v-row>
        </div>
        <v-row>
            <v-col cols="12" xl="3" lg="4" md="6">
                <v-card flat outlined>
                    <v-card-text>
                        <v-row dense wrap>
                            <v-col cols="12">
                                <InputField
                                    :readonly="!inEditMode"
                                    v-model="project.name"
                                    :errorBorder="!projectValidation.name"
                                    :label="'* ' + $t('fields.name')"
                                ></InputField>
                            </v-col>
                            <v-col cols="12">
                                <Dropdown
                                    :readonly="!inEditMode"
                                    v-model="project.user_id"
                                    :ajax="true"
                                    :data="'users'"
                                    :errorBorder="!projectValidation.user_id"
                                    :label="'* ' + $t('fields.user')"
                                ></Dropdown>
                            </v-col>
                            <v-col cols="12">
                                <DatePicker
                                    :readonly="!inEditMode"
                                    v-model="project.date_created"
                                    :errorBorder="!projectValidation.date_created"
                                    :date-label="$t('fields.dateCreated')"
                                ></DatePicker>
                            </v-col>
                            <v-col cols="12">
                                <DateTimePicker
                                    :readonly="!inEditMode"
                                    v-model="project.deadline"
                                    :errorBorder="!projectValidation.deadline"
                                    :date-label="$t('fields.deadline')"
                                ></DateTimePicker>
                            </v-col>
                            <v-col cols="12">
                                <CurrencyField
                                    :readonly="!inEditMode"
                                    v-model="project.amount"
                                    :errorBorder="!projectValidation.amount"
                                    :label="$t('fields.amount')"
                                ></CurrencyField>
                            </v-col>
                            <v-col cols="12">
                                <Upload
                                    :readonly="!inEditMode"
                                    v-model="project.uploaded_file"
                                    :errorBorder="!projectValidation.uploaded_file"
                                ></Upload>
                            </v-col>
                            <v-col cols="12">
                                <CheckBox
                                    :readonly="!inEditMode"
                                    v-model="project.active"
                                    :label="$t('fields.active')"
                                ></CheckBox>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import InputField from './trinkets/InputField';
import CheckBox from './trinkets/CheckBox';
import Dropdown from './trinkets/Dropdown';
import Project from '../models/Project';
import DatePicker from '@/components/trinkets/DatePicker';
import DateTimePicker from '@/components/trinkets/DateTimePicker';
import Upload from '@/components/trinkets/Upload';
import CurrencyField from '@/components/trinkets/CurrencyField';

export default {
    name: 'Project',
    components: {CurrencyField, Upload, DateTimePicker, DatePicker, CheckBox, InputField, Dropdown},
    data: function () {
        return {
            project: new Project({id: this.$route.params.id}),
            projectValidation: new Project({}, true),
            initialLoad: false,
            inEditMode: false,
            lockKey: 'project',
            lockTimer: null,
        };
    },
    methods: {
        editClick() {
            let vm = this;
            this.setLock((data) => {
                if (data.success) {
                    vm.inEditMode = true;
                    vm.lockTimer = setInterval(() => {
                        vm.setLock();
                    }, 10000);
                } else {
                    vm.$alert(
                        this.$t('alerts.recordLocked')
                            .split('%name%')
                            .join(data.locked_by.first_name + ' ' + data.locked_by.last_name),
                    );
                }
            });
        },
        setLock(after = null) {
            this.$APICall('post', 'lock', {key: this.lockKey + '|' + this.project.id}, (data, success) => {
                if (success) {
                    if (after !== null) {
                        after(data);
                    }
                }
            });
        },
        removeLock() {
            this.$APICall('delete', 'lock', {key: this.lockKey + '|' + this.project.id}, () => {});
        },
        backClick() {
            this.$router.go(-1);
        },
        saveClick(after = null) {
            let success = true;

            if (this.project.name == '') {
                success = false;
                this.projectValidation.name = false;
            } else {
                this.projectValidation.name = true;
            }

            if (this.project.user_id == null) {
                success = false;
                this.projectValidation.user_id = false;
            } else {
                this.projectValidation.user_id = true;
            }

            if (success) {
                let vm = this;
                let action = 'post';
                let addition = '';
                if (vm.project.id != 0) {
                    action = 'put';
                    addition = '/' + vm.project.id;
                }
                this.$APICall(action, 'projects' + addition, this.project, (data, success) => {
                    if (success) {
                        vm.inEditMode = false;
                        if (vm.project.id == 0) {
                            vm.$notify(vm.$t('alerts.basicAdd'));
                        } else {
                            vm.$notify(vm.$t('alerts.basicUpdate'));
                        }
                        vm.$triggerReload();
                        if (after !== null) {
                            after();
                        } else {
                            vm.backClick();
                        }
                    } else {
                        vm.$alert(vm.$t('alerts.projectExists'));
                    }
                });
            }
        },
    },
    beforeRouteLeave(to, from, next) {
        if (this.inEditMode) {
            let vm = this;
            this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
                if (result) {
                    if (vm.lockTimer !== null) clearInterval(this.lockTimer);
                    vm.removeLock();
                    next(true);
                } else {
                    next(false);
                }
            });
        } else {
            next(true);
        }
    },
    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
        }
        let vm = this;
        if (this.project.id != 0) {
            this.$APICall('get', 'projects/' + this.project.id, {}, (data, success) => {
                if (success) {
                    let usableData = JSON.parse(JSON.stringify(data));
                    vm.$myStore.commit('setBreadcrumbs', [
                        {
                            text: vm.$t('modules.projects'),
                            href: '/#/projects',
                            disabled: false,
                        },
                        {
                            text: vm.project.email,
                            href: '/#/projects/' + vm.project.id,
                            disabled: true,
                        },
                    ]);
                    vm.$nextTick(() => {
                        vm.initialLoad = true;
                    });
                    vm.project = usableData;
                }
            });
        } else {
            this.$myStore.commit('setBreadcrumbs', [
                {
                    text: this.$t('modules.projects'),
                    href: '/#/projects',
                    disabled: false,
                },
                {
                    text: this.$t('modules.newProject'),
                    href: '/#/projects/0',
                    disabled: true,
                },
            ]);
            this.inEditMode = true;
            this.initialLoad = true;
        }
    },
};
</script>

<style scoped></style>
